import { getSurfaceTokens } from './tokens';
import type { Theme } from '@mui/material/styles';

const resolveSurfaceTokens = (theme: Theme): ReturnType<typeof getSurfaceTokens> =>
  getSurfaceTokens(theme.palette.mode);

export const semanticColors = {
  surface: (theme: Theme): string => resolveSurfaceTokens(theme).paper,
  surfaceSubtle: (theme: Theme): string => resolveSurfaceTokens(theme).paper,
  pageBg: (theme: Theme): string => resolveSurfaceTokens(theme).page,
  pageAltBg: (theme: Theme): string => resolveSurfaceTokens(theme).pageAlt,
  pageMutedBg: (theme: Theme): string => resolveSurfaceTokens(theme).pageMuted,
  surfacePaper: (theme: Theme): string => resolveSurfaceTokens(theme).paper,
  border: (theme: Theme): string => theme.palette.divider,
  textPrimary: (theme: Theme): string => theme.palette.text.primary,
  textSecondary: (theme: Theme): string => theme.palette.text.secondary,
  actionHover: (theme: Theme): string => theme.palette.action.hover,
  actionSelected: (theme: Theme): string => theme.palette.action.selected,
  accent: (theme: Theme): string => theme.palette.primary.main,
  accentContrast: (theme: Theme): string => theme.palette.primary.contrastText,
  accentStrong: (theme: Theme): string => theme.palette.primary.dark,
  success: (theme: Theme): string => theme.palette.success.main,
  successStrong: (theme: Theme): string => theme.palette.success.dark,
  warning: (theme: Theme): string => theme.palette.warning.main,
  brown: (theme: Theme): string => theme.palette.brown.main,
  error: (theme: Theme): string => theme.palette.error.main,
  errorStrong: (theme: Theme): string => theme.palette.error.dark,
  info: (theme: Theme): string => theme.palette.info.main,
  successBg: (theme: Theme): string => theme.palette.success.light,
  warningBg: (theme: Theme): string => theme.palette.warning.light,
  errorBg: (theme: Theme): string => theme.palette.error.light,
  infoBg: (theme: Theme): string => theme.palette.info.light,
} as const;

export type SemanticColorKey = keyof typeof semanticColors;

const resolvePalettePathColor = (theme: Theme, key: string): string | null => {
  const segments = key.split('.');
  let current: unknown = theme.palette;

  for (const segment of segments) {
    if (typeof current !== 'object' || current === null || !(segment in current)) {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return typeof current === 'string' ? current : null;
};

export const resolveSemanticColor = (theme: Theme, key: string): string => {
  const resolver = (semanticColors as Record<string, ((theme: Theme) => string) | undefined>)[key];
  if (typeof resolver === 'function') {
    return resolver(theme);
  }

  const palettePathColor = resolvePalettePathColor(theme, key);
  if (typeof palettePathColor === 'string') {
    return palettePathColor;
  }

  return key;
};

export const resolveContrastText = (theme: Theme, background: string): string =>
  theme.palette.getContrastText(background);

export const isLightTheme = (theme: Theme): boolean => {
  const lightSurfaceTokens = getSurfaceTokens('light');
  return resolveSurfaceTokens(theme).page === lightSurfaceTokens.page;
};
